import { useCallback, useMemo, useState } from 'react';
import { visibleItems, visibleLists } from '@shared/merge.ts';
import { clearChecked, moveItem } from './store/state.ts';
import { useReorder } from './hooks/useReorder.ts';
import { useStore, useTicker, useUpdateAvailable } from './hooks/useStore.ts';
import { useViewportHeight } from './hooks/useViewportHeight.ts';
import { applyUpdate } from './lib/update.ts';
import ItemInput from './components/ItemInput.tsx';
import ItemRow from './components/ItemRow.tsx';
import ListSelector from './components/ListSelector.tsx';
import Settings from './components/Settings.tsx';
import SyncBadge from './components/SyncBadge.tsx';

export default function App() {
  const { ready, view, selectedListId, pendingCount, status } = useStore();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const updateAvailable = useUpdateAvailable();

  // Rafraîchit « il y a 5 min » sans qu'aucune donnée n'ait changé.
  useTicker();
  // Empêche le clavier de recouvrir le champ de saisie.
  useViewportHeight();

  const lists = visibleLists(view);
  const items = useMemo(
    () => (selectedListId ? visibleItems(view, selectedListId) : []),
    [view, selectedListId],
  );
  const checked = items.filter((item) => item.is_checked);

  const ids = useMemo(() => items.map((item) => item.id), [items]);
  const byId = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);

  const reorder = useReorder({
    ids,
    // Deux groupes seulement : cochés et non cochés. Un article ne peut pas
    // être déposé dans l'autre, l'affichage l'y remettrait aussitôt.
    groupOf: useCallback((id: string) => (byId.get(id)?.is_checked ? 'checked' : 'open'), [byId]),
    onMove: useCallback(
      (id: string, target: number) => {
        const item = byId.get(id);
        if (item) void moveItem(item, target);
      },
      [byId],
    ),
  });

  if (!ready) return <main className="empty-screen" aria-busy="true" />;

  return (
    <div className="app">
      <header className="app__header">
        <ListSelector lists={lists} selectedId={selectedListId} onOpenSettings={() => setSettingsOpen(true)} />
        <SyncBadge status={status} pendingCount={pendingCount} />
        {updateAvailable && (
          <button type="button" className="badge badge--action" onClick={() => void applyUpdate()}>
            Une nouvelle version est prête — appuie pour l’installer
          </button>
        )}
      </header>

      <main className="app__main">
        {selectedListId === null ? (
          <p className="app__empty">Crée une première liste avec le bouton +.</p>
        ) : (
          <>
            {items.length === 0 ? (
              <p className="app__empty">Liste vide. Ajoute un article ci-dessous.</p>
            ) : (
              <ul {...reorder.listProps}>
                {items.map((item, index) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    devices={view.devices}
                    handle={reorder.row(item.id, index)}
                  />
                ))}
              </ul>
            )}

            {checked.length > 0 && (
              <button
                type="button"
                className="button button--ghost app__clear"
                onClick={() => void clearChecked(selectedListId)}
              >
                Vider les {checked.length} article{checked.length > 1 ? 's' : ''} coché
                {checked.length > 1 ? 's' : ''}
              </button>
            )}
          </>
        )}
      </main>

      {selectedListId !== null && (
        <footer className="app__footer">
          <ItemInput view={view} listId={selectedListId} />
        </footer>
      )}

      {settingsOpen && (
        <Settings lists={lists} devices={[...view.devices.values()]} onClose={() => setSettingsOpen(false)} />
      )}
    </div>
  );
}
